import React from 'react';
import { Col, Row } from '../../../lib/utils';
import { colors } from '../../../lib/colors';
import { CaptionLight, Subtitle1Bold, Body2Regular } from '../../../lib/font';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { ICenterMember } from '@/features/publicMember/types';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

const TYPE_COLORS: Record<string, string> = {
    public: '#3B82F6',
    pt: '#10B981',
};

const TYPE_LABEL: Record<string, string> = {
    public: '퍼블릭 회원',
    pt: '수강 회원',
};

const STATUS_COLORS: Record<string, string> = {
    active: '#10B981',
    inactive: '#9CA3AF',
};

const STATUS_LABEL: Record<string, string> = {
    active: '활성',
    inactive: '비활성',
};

const MemberStatsTab = ({ centerId }: { centerId: string }) => {
    // 전체 회원 조회 (limit 크게 잡아서 전부 가져옴)
    const { data: memberData, isLoading } = usePublicMemberList({
        centerId,
        memberType: 'all',
        status: 'all',
        limit: 9999,
    });

    const members: ICenterMember[] = memberData?.results ?? [];
    const totalMembers = members.length;

    // 타입별 집계 (temp + original = 수강 회원)
    const typeCount: Record<string, number> = { public: 0, pt: 0 };
    const statusCount: Record<string, number> = { active: 0, inactive: 0 };
    members.forEach((m) => {
        const type = m.memberType === 'public' ? 'public' : 'pt';
        typeCount[type] = (typeCount[type] || 0) + 1;
        const status = m.status === 'active' ? 'active' : 'inactive';
        statusCount[status] = (statusCount[status] || 0) + 1;
    });

    const typePieData = Object.entries(typeCount)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => ({
            name: TYPE_LABEL[key] || key,
            value: count,
            color: TYPE_COLORS[key] || '#9CA3AF',
        }));

    const statusPieData = Object.entries(statusCount)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => ({
            name: STATUS_LABEL[key] || key,
            value: count,
            color: STATUS_COLORS[key] || '#9CA3AF',
        }));

    const activeCount = statusCount['active'] || 0;
    const inactiveCount = statusCount['inactive'] || 0;

    if (isLoading) {
        return (
            <Col className="items-center justify-center py-[80px]">
                <Body2Regular color={colors.LAVEL_1}>데이터를 불러오는 중...</Body2Regular>
            </Col>
        );
    }

    return (
        <Col>
            {/* KPI 카드 */}
            <Row style={{ marginTop: 16, gap: 16 }}>
                <KpiCard label="총 회원 수" value={totalMembers} unit="명" bgColor="#EFF6FF" textColor="#2563EB" />
                <KpiCard label="퍼블릭 회원" value={typeCount['public'] || 0} unit="명" bgColor="#DBEAFE" textColor="#1D4ED8" />
                <KpiCard label="수강 회원" value={typeCount['pt'] || 0} unit="명" bgColor="#D1FAE5" textColor="#065F46" />
                <KpiCard label="비활성" value={inactiveCount} unit="명" bgColor="#F5F5F5" textColor="#6B7280" />
            </Row>

            {/* 차트 영역 */}
            <Row style={{ marginTop: 32, gap: 24, alignItems: 'flex-start' }}>
                {/* 회원 타입별 분포 */}
                <Col className="flex-1">
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        회원 타입별 분포
                    </Body2Regular>
                    {typePieData.length > 0 ? (
                        <Col className="items-center">
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={typePieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {typePieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any, name: any) => [
                                            `${Number(value).toLocaleString()}명 (${totalMembers ? ((Number(value) / totalMembers) * 100).toFixed(1) : 0}%)`,
                                            name,
                                        ]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <Row style={{ gap: 24, marginTop: -8 }}>
                                {typePieData.map((d) => (
                                    <Row key={d.name} style={{ alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: d.color }} />
                                        <CaptionLight color={colors.LAVEL_2}>
                                            {d.name}: {d.value.toLocaleString()}명
                                        </CaptionLight>
                                    </Row>
                                ))}
                            </Row>
                        </Col>
                    ) : (
                        <EmptyState />
                    )}
                </Col>

                {/* 상태별 분포 */}
                <Col className="flex-1">
                    <Body2Regular color={colors.LAVEL_2} className="mb-[12px]">
                        회원 상태별 분포
                    </Body2Regular>
                    {statusPieData.length > 0 ? (
                        <Col className="items-center">
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={statusPieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        dataKey="value"
                                    >
                                        {statusPieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                        formatter={(value: any, name: any) => [
                                            `${Number(value).toLocaleString()}명 (${totalMembers ? ((Number(value) / totalMembers) * 100).toFixed(1) : 0}%)`,
                                            name,
                                        ]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <Row style={{ gap: 24, marginTop: -8 }}>
                                {statusPieData.map((d) => (
                                    <Row key={d.name} style={{ alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: d.color }} />
                                        <CaptionLight color={colors.LAVEL_2}>
                                            {d.name}: {d.value.toLocaleString()}명
                                        </CaptionLight>
                                    </Row>
                                ))}
                            </Row>
                        </Col>
                    ) : (
                        <EmptyState />
                    )}
                </Col>
            </Row>
        </Col>
    );
};

const KpiCard = ({ label, value, unit, bgColor, textColor }: {
    label: string; value: number | string; unit?: string; bgColor: string; textColor: string;
}) => (
    <Col className="flex-1" style={{ backgroundColor: bgColor, borderRadius: 12, padding: 20 }}>
        <CaptionLight color={colors.LAVEL_1}>{label}</CaptionLight>
        <Subtitle1Bold className="mt-[4px]" style={{ color: textColor }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
            {unit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 2 }}>{unit}</span>}
        </Subtitle1Bold>
    </Col>
);

const EmptyState = () => (
    <Col style={{ alignItems: 'center', justifyContent: 'center', padding: '60px 0', backgroundColor: '#F8F9FA', borderRadius: 12 }}>
        <Body2Regular color={colors.LAVEL_1}>데이터가 없습니다</Body2Regular>
        <CaptionLight color={colors.LAVEL_1} className="mt-[4px]">회원을 등록하면 통계가 표시됩니다</CaptionLight>
    </Col>
);

export default MemberStatsTab;
